import React, { useReducer, useRef } from 'react';
import cn from 'classnames';
import { CheckIcon, LoaderIcon } from '@repo/icons';
import { UserSession } from '@repo/utils/types';
import { apiRequest } from '@repo/utils/helpers/api';
import { Section, SettingsSectionBox, SettingsSectionLabel } from '@views/Settings/components';
import styles from './PreferredEmail.module.scss';
import { useAuthContext } from '@repo/hooks';

interface EmailSettingState {
  editing: boolean;
  saving: boolean;
  value: string;
  initialValue: string;
  isChanged: boolean;
  errorMsg?: string;
  loadError: boolean;
}

const reducer = (state: EmailSettingState, [type, value]: [string, any?]): EmailSettingState => {
  switch (type) {
    case 'currentEmailLoaded':
      return {
        ...state,
        saving: false,
        value,
        initialValue: value,
        isChanged: false,
      };

    case 'edit':
      return { ...state, value, isChanged: value != state.initialValue, errorMsg: null };

    case 'save':
      return { ...state, editing: false, saving: true };

    case 'cancel':
      return {
        ...state,
        value: state.initialValue,
        editing: false,
        errorMsg: null,
        isChanged: false,
      };

    case 'error':
      return {
        ...state,
        saving: false,
        value: state.initialValue,
        isChanged: false,
        errorMsg: 'Something went wrong when saving your email! Please try again.',
      };

    case 'validationError':
      return { ...state, errorMsg: value };

    case 'loadError':
      return { ...state, loadError: true };

    default:
      return state;
  }
};

export default () => {
  const { user, updateLocalUserProfile } = useAuthContext();
  const [{ value, saving, isChanged, errorMsg, loadError }, dispatch] = useReducer(reducer, {
    editing: false,
    saving: false,
    value: user?.email || '',
    initialValue: user?.email || '',
    isChanged: false,
    loadError: false,
  });
  const inputRef = useRef(null);

  const saveChanges = () => {
    // check validity of the input
    if (inputRef.current && inputRef.current.validity.typeMismatch) {
      return dispatch(['validationError', 'Please enter a valid email']);
    }

    if (inputRef.current && inputRef.current.validity.valueMissing) {
      return dispatch(['validationError', 'A notification email is required']);
    }

    const originalEmail = user.email;
    updateLocalUserProfile({ email: value });
    dispatch(['save']);
    apiRequest<UserSession>('PATCH', '/users/self/', { email: value })
      .then((user) => {
        dispatch(['currentEmailLoaded', user.email || '']);
      })
      .catch(() => {
        updateLocalUserProfile({ email: originalEmail });
        dispatch(['error']);
      });
  };

  const handleInputKeyDown = (e) => {
    // "Enter"
    if (e.keyCode == 13) {
      saveChanges();
    }

    // "Esc"
    if (e.keyCode == 27) {
      dispatch(['cancel']);
    }
  };

  return (
    !loadError && (
      <Section>
        <SettingsSectionLabel error={errorMsg}>Preferred Email</SettingsSectionLabel>
        <SettingsSectionBox className={styles.inputWrapper}>
          <input
            className={cn(styles.textInput, isChanged && styles.dirtyInput)}
            type="email"
            required
            ref={inputRef}
            value={value}
            placeholder={`Enter Preferred Email Address`}
            onChange={(e) => dispatch(['edit', e.target.value])}
            onKeyDown={handleInputKeyDown}
            disabled={saving}
          />
          <div className={styles.btnContainer}>
            {saving && <LoaderIcon />}
            {isChanged && value.length > 0 && !saving && (
              <CheckIcon width="14" className={styles.iconBtn} tabIndex={0} onClick={saveChanges} />
            )}
          </div>
        </SettingsSectionBox>
      </Section>
    )
  );
};
